import { Assistant } from "@gram/client/models/components/assistant.js";
import {
  invalidateAllAssistantsGet,
  queryKeyAssistantsGet,
  useAssistantsGet,
} from "@gram/client/react-query/assistantsGet.js";
import { invalidateAllAssistantsList } from "@gram/client/react-query/assistantsList.js";
import { invalidateAllListEnvironments } from "@gram/client/react-query/listEnvironments.js";
import { invalidateAllListToolsets } from "@gram/client/react-query/listToolsets.js";
import { invalidateAllTrigger } from "@gram/client/react-query/trigger.js";
import { invalidateAllTriggers } from "@gram/client/react-query/triggers.js";
import { invalidateAllSkill } from "@gram/client/react-query/skill.js";
import { useQueryClient } from "@tanstack/react-query";
import { useProject } from "@/contexts/Auth";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AssistantDraftCtx,
  AssistantEnv,
  DraftContextValue,
  PendingResolver,
} from "./useAssistantDraft";

export function AssistantDraftProvider({
  initialAssistantId,
  onAssistantCreated,
  children,
}: {
  initialAssistantId: string | null;
  onAssistantCreated?: (id: string) => void;
  children: ReactNode;
}): JSX.Element {
  const queryClient = useQueryClient();
  const project = useProject();
  const [assistantId, setAssistantIdState] = useState<string | null>(
    initialAssistantId,
  );
  const [assistantEnv, setAssistantEnvState] = useState<AssistantEnv | null>(
    null,
  );
  const pendingRef = useRef(new Map<string, PendingResolver>());

  const { data: assistant, refetch } = useAssistantsGet(
    { id: assistantId ?? "", gramProject: project.slug },
    undefined,
    {
      enabled: !!assistantId,
      retry: false,
      throwOnError: false,
      refetchOnWindowFocus: false,
    },
  );

  const setAssistantId = useCallback(
    (id: string) => {
      setAssistantIdState((prev) => {
        if (prev === id) return prev;
        onAssistantCreated?.(id);
        return id;
      });
    },
    [onAssistantCreated],
  );

  const setAssistant = useCallback(
    (next: Assistant) => {
      queryClient.setQueryData(
        queryKeyAssistantsGet({ id: next.id, gramProject: project.slug }),
        next,
      );
      setAssistantIdState((prev) => {
        if (prev === next.id) return prev;
        onAssistantCreated?.(next.id);
        return next.id;
      });
    },
    [queryClient, onAssistantCreated, project.slug],
  );

  const invalidateAll = useCallback(() => {
    void invalidateAllAssistantsGet(queryClient);
    void invalidateAllAssistantsList(queryClient);
    void invalidateAllListEnvironments(queryClient);
    void invalidateAllListToolsets(queryClient);
    void invalidateAllTriggers(queryClient);
    void invalidateAllTrigger(queryClient);
  }, [queryClient]);

  const invalidateSkillAttachments = useCallback(() => {
    void invalidateAllAssistantsList(queryClient);
    void invalidateAllSkill(queryClient);
  }, [queryClient]);

  const registerPending = useCallback(
    (toolCallId: string, resolver: PendingResolver) => {
      pendingRef.current.set(toolCallId, resolver);
    },
    [],
  );

  const resolvePending = useCallback((toolCallId: string, result: unknown) => {
    const resolver = pendingRef.current.get(toolCallId);
    if (!resolver) return false;
    pendingRef.current.delete(toolCallId);
    resolver(result);
    return true;
  }, []);

  const setAssistantEnv = useCallback((env: AssistantEnv | null) => {
    setAssistantEnvState(env);
  }, []);

  useEffect(() => {
    if (assistantEnv) return;
    const slugs = (assistant?.toolsets ?? [])
      .map((t) => t.environmentSlug)
      .filter((s): s is string => !!s);
    if (slugs.length === 0) return;
    const first = slugs[0]!;
    if (slugs.some((s) => s !== first)) return;
    setAssistantEnvState({ id: "", slug: first });
  }, [assistant, assistantEnv]);

  const value = useMemo<DraftContextValue>(
    () => ({
      assistantId,
      setAssistantId,
      setAssistant,
      assistant,
      refetchAssistant: refetch,
      invalidateAll,
      invalidateSkillAttachments,
      registerPending,
      resolvePending,
      assistantEnv,
      setAssistantEnv,
    }),
    [
      assistantId,
      setAssistantId,
      setAssistant,
      assistant,
      refetch,
      invalidateAll,
      invalidateSkillAttachments,
      registerPending,
      resolvePending,
      assistantEnv,
      setAssistantEnv,
    ],
  );

  return (
    <AssistantDraftCtx.Provider value={value}>
      {children}
    </AssistantDraftCtx.Provider>
  );
}
